import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { formatDuration, intervalToDuration } from 'date-fns';
import { useIdleTimer } from 'react-idle-timer';
import { signOut, useSession } from 'next-auth/react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

const IDLE_TIMEOUT_IN_MS =
  parseInt(process.env.NEXT_PUBLIC_IDLE_TIMEOUT ?? '900', 10) * 1000;
const IDLE_PROMPT_TIMEOUT_IN_MS =
  parseInt(process.env.NEXT_PUBLIC_IDLE_PROMPT_TIMEOUT ?? '600', 10) * 1000;

const IdleManager = ({ children }: PropsWithChildren<{}>) => {
  const { status } = useSession();
  const [showPrompt, setShowPrompt] = useState(false);
  const [remainingTime, setRemainingTime] = useState(IDLE_TIMEOUT_IN_MS);

  const onIdle = useCallback(async () => {
    setShowPrompt(false);
    await signOut();
  }, []);

  const { reset: resetIdle, getRemainingTime } = useIdleTimer({
    timeout: IDLE_TIMEOUT_IN_MS,
    onIdle,
  });

  const onIdleForPrompt = useCallback(() => setShowPrompt(true), []);
  const { reset: resetPrompt } = useIdleTimer({
    timeout: IDLE_PROMPT_TIMEOUT_IN_MS,
    onIdle: onIdleForPrompt,
  });

  const onResumeSession = useCallback(() => {
    resetIdle();
    resetPrompt();
    setShowPrompt(false);
  }, [resetIdle, resetPrompt]);

  useEffect(() => {
    setInterval(() => setRemainingTime(getRemainingTime()), 1000);
  }, []);

  const IdleDialog = (
    <Dialog
      maxWidth="xs"
      onClose={onResumeSession}
      open={status === 'authenticated'}
    >
      <DialogTitle>Are you still here?</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body2">
          You will be automatically signed out to protect your information in{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDuration(
              intervalToDuration({ start: 0, end: remainingTime })
            )}
          </strong>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => signOut} color="inherit">
          Sign out now
        </Button>
        <Button onClick={onResumeSession} color="primary">
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {showPrompt && IdleDialog}
      {children}
    </>
  );
};

export default IdleManager;
